import globalAxios from 'axios'

/**
 * Shared radar emails (ShareRadarReportMail) append `?user=<recipient uuid>` to the deep link.
 * The auth bootstrap uses it to resolve the recipient's Cognito pool — but ONLY while logged
 * out. A visitor who already has a session for a different account keeps that session, and the
 * radar page then renders against the wrong identity (empty list, no explanation).
 *
 * Mirrors `src/lib/sharedLink.ts` in forshipping-spa; both hosts serve shared radar links.
 */
export const SHARED_LINK_USER_PARAM = 'user'

const ACK_STORAGE_PREFIX = 'sharedLink:ack:'

export interface LinkRecipient {
	maskedEmail: string | null
	companyName: string | null
}

type RouteQuery = Record<string, string | Array<string | null> | null | undefined>

/** The uuid the link was addressed to, or null when this is not a shared-link visit. */
export function readIntendedRecipient(query: RouteQuery): string | null {
	const raw = query[SHARED_LINK_USER_PARAM]
	const value = Array.isArray(raw) ? raw[0] : raw

	return typeof value === 'string' && value.trim() !== '' ? value.trim() : null
}

/**
 * "Continue anyway" is remembered per tab (sessionStorage) so in-app navigation doesn't
 * re-ask on every hop.
 */
export function isAcknowledged(uuid: string): boolean {
	try {
		return window.sessionStorage.getItem(ACK_STORAGE_PREFIX + uuid) !== null
	} catch {
		return false
	}
}

export function acknowledge(uuid: string): void {
	try {
		window.sessionStorage.setItem(ACK_STORAGE_PREFIX + uuid, '1')
	} catch {
		// Private-mode storage failure only costs us a repeated prompt.
	}
}

/**
 * Ask the API which mailbox the link belongs to. Authenticated (globalAxios carries the
 * bearer via main.ts's request interceptor) + throttled endpoint answering with a MASKED
 * address, so a link holder can never read the address itself.
 *
 * Returns null when the lookup fails — the dialog then falls back to generic copy.
 */
export async function fetchLinkRecipient(uuid: string): Promise<LinkRecipient | null> {
	const base = (process.env.VUE_APP_API_URL || '/api/v3/').replace(/\/$/, '')

	try {
		const { data } = await globalAxios.get<Partial<LinkRecipient>>(
			`${base}/auth/link-recipient/${encodeURIComponent(uuid)}`
		)

		return {
			maskedEmail: typeof data.maskedEmail === 'string' ? data.maskedEmail : null,
			companyName: typeof data.companyName === 'string' ? data.companyName : null
		}
	} catch {
		return null
	}
}
